import React, { useState } from "react";
import { Upload, Loader2, Trash2, ExternalLink } from "lucide-react";
import { api, formatApiError, resolveUploadUrl } from "@/lib/api";

/** File upload with paste-URL fallback.
 *  Stores the resolved URL string in the parent's value.
 *
 *  Props: value (string), onChange(string), accept (string), label
 */
export const UploadPicker = ({ value, onChange, accept = "image/*", label = "" }) => {
    const [uploading, setUploading] = useState(false);
    const [err, setErr] = useState("");

    const handleFile = async (file) => {
        if (!file) return;
        setErr("");
        setUploading(true);
        try {
            const form = new FormData();
            form.append("file", file);
            const { data } = await api.post("/admin/uploads", form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            onChange(data.url);
        } catch (e) {
            setErr(formatApiError(e));
        } finally {
            setUploading(false);
        }
    };

    const isUpload = value && value.startsWith("/api/");
    const previewUrl = resolveUploadUrl(value);
    const isImage = accept.includes("image");

    return (
        <div className="space-y-2">
            {label && <label className="block text-[12.5px] font-medium text-slate-700">{label}</label>}
            <div className="flex gap-2 items-start">
                <input
                    type="text"
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder="https://… or upload below"
                    className="flex-1 border border-slate-300 px-3 py-2 text-[13.5px] focus:outline-none focus:border-teal-600"
                />
                <label className="inline-flex items-center gap-1.5 px-3 py-2 text-[13px] border border-slate-300 bg-slate-50 hover:bg-slate-100 cursor-pointer text-slate-700">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    <span>{uploading ? "Uploading…" : "Upload"}</span>
                    <input
                        type="file"
                        accept={accept}
                        className="hidden"
                        disabled={uploading}
                        onChange={(e) => handleFile(e.target.files?.[0])}
                    />
                </label>
                {value && (
                    <button
                        type="button"
                        onClick={() => onChange("")}
                        className="inline-flex items-center gap-1 px-2 py-2 text-[13px] border border-slate-300 bg-white text-slate-600 hover:bg-slate-50"
                        title="Clear"
                    >
                        <Trash2 className="w-4 h-4" />
                    </button>
                )}
            </div>
            {err && <p className="text-[12.5px] text-red-600">{err}</p>}
            {previewUrl && isImage && (
                <div className="border border-slate-200 p-2 bg-slate-50 inline-block">
                    <img src={previewUrl} alt="" className="max-h-24 object-contain" onError={(e) => { e.target.style.display = "none"; }} />
                </div>
            )}
            {previewUrl && !isImage && (
                <a href={previewUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[12.5px] text-teal-700 hover:underline">
                    <ExternalLink className="w-3.5 h-3.5" /> Open file
                </a>
            )}
            {isUpload && <p className="text-[11px] text-slate-500">Stored as internal upload — safe for production.</p>}
        </div>
    );
};

export default UploadPicker;
